import { AxiosError } from "axios";

import shipvistaAPIv2 from "./shipvistaAPIv2";
import {
  V2QuoteRequestBody,
  V2RateShopResult,
} from "@/mappers/rating/v2GuestQuoteMapper";

/**
 * NEXT-GUEST-QUOTES-V2 — the v2 guest-quote API surface.
 *
 * `POST /v2/rating/quotes/guest` (ShipvistaAPI 06-rating.md §6.1, GLM-96 guest tenant):
 *   - Anonymous: no `Authorization` header, no tenant id in the body — the backend resolves the
 *     fixed guest tenant (a1b2c3d4-0000-0000-0000-000000000096) that owns the house carrier
 *     accounts an anonymous quote is allowed to see.
 *   - 200 → `RateShopResult` `{ quotes, carrierErrors }` (per-carrier failures ride IN-BAND; the
 *     empty-quotes cases are 422/502, never a `status:false`-on-200).
 *   - 4xx/422/502 → RFC 7807 `application/problem+json` `{ type, title, status, detail, code }`.
 *
 * The v1 `200 + status:false` trap is gone: this client throws on every non-2xx (axios default) and
 * `mapGuestQuoteError` normalizes the problem+json into a single friendly message.
 */

/** RFC 7807 shape returned on any 4xx/422/502 from the v2 rating endpoints. */
export interface RatingProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  /** ShipVista problem code (e.g. `validation-failed`, `no-carrier-accounts`). */
  code?: string;
}

/** Known rating error codes → user-facing copy (ERR-UX: friendly, never raw carrier/problem detail). */
const RATING_MESSAGES: Record<string, string> = {
  "validation-failed":
    "Please check the addresses and package details, then try again.",
  "no-carrier-accounts":
    "We couldn't find any carrier services for that route. Try a different origin or destination.",
  "all-carriers-unavailable":
    "Our carriers are taking longer than usual to respond. Please try again in a moment.",
};

/**
 * Normalise a thrown Axios error (v2 problem+json) into one user-facing message, plus the raw code
 * so the caller can branch if it needs to (e.g. count carrier errors). Mirrors `mapAuthError`'s
 * shape so the alert-bar call sites are symmetric across the v2 surfaces.
 */
export function mapGuestQuoteError(
  error: unknown,
  fallback = "We couldn't get quotes right now. Please try again later."
): { message: string; code?: string; status?: number } {
  const ax = error as AxiosError<RatingProblemDetails>;
  const problem = ax?.response?.data;
  const status = ax?.response?.status;

  if (problem && typeof problem === "object") {
    const code = problem.code;
    if (code && RATING_MESSAGES[code])
      return { message: RATING_MESSAGES[code], code, status };
    // Fall back to the server's high-level detail (CORE-2's ERR-UX Scope A will gate the verbose
    // detail server-side; in prod this is already the safe, high-level string).
    if (problem.detail) return { message: problem.detail, code, status };
    if (problem.title) return { message: problem.title, code, status };
  }
  if (status === 429) {
    return {
      message: "Too many quote requests. Please wait a moment and try again.",
      status,
    };
  }
  return { message: fallback, status };
}

/** Count the in-band per-carrier failures on a successful 200 (for the "N carriers unavailable" note). */
export function countCarrierErrors(
  result: V2RateShopResult | undefined
): number {
  return result?.carrierErrors?.length ?? 0;
}

/**
 * The one guest-quote call. Returns the raw `RateShopResult` (the caller maps it via
 * `mapGuestQuoteResponse`). Anonymous — no auth header is attached by the caller; `shipvistaAPIv2`
 * is the shared pre-login client (no interceptor adds a Bearer).
 */
export const getGuestQuote = (body: V2QuoteRequestBody) =>
  shipvistaAPIv2.post<V2RateShopResult>("/rating/quotes/guest", body);
